import React, { useState, useEffect, useRef } from 'react'

import { TextInput, ToggleSwitch, Select, Field, ValidationMessage, FieldLabel, Checkbox } from '../../../../../../index'
import { isPositiveNumber, getNewImageSize, toFixedIfDecimal } from './utils'
import styles from './style.module.css'

type naturalImageWHTypes = {
    width: string;
    height: string;
    widthScale?: number;
    heightScale?: number;
}

const ImageSizeField = ({ attributes, naturalImageWH, hasError, updateFormData }) => {
    const oldWHRef = useRef<naturalImageWHTypes>({ width: '', height: '' })
    const [isAuto, setIsAuto] = useState(!attributes.width && !attributes.height)
    const [formData, setFormData] = useState({
        height: attributes.height || '',
        width: attributes.width || '',
        lockAspectRatio: false
    })

    const [errorMessage, setErrorMessages] = useState({
        width: '',
        height: '',
    })

    const handleToggle = (e) => {
        e.persist()
        const targetName = e.target.name
        const targetValue = e.target.checked

        setFormData((prevState) => {
            return { ...prevState, [targetName]: targetValue }
        })
    }

    const handleErrorOnWidthHeight = (value) => {
        const width = value.width;
        const height = value.height;
        let err: Record<string, string> = {};
        if (value.hasOwnProperty('width')) {
            if (!width) {
                err.width = 'Please enter a valid pixel value';
            } else if (width && !isPositiveNumber(width)) {
                err.width = 'Please enter a valid pixel value';
            } else if (width && width < 15) {
                err.width = 'Please enter a value greater than 14';
            } else err.width = '';
        }

        if (value.hasOwnProperty('height')) {
            if (!height) {
                err.height = 'Please enter a valid pixel value';
            } else if (height && !isPositiveNumber(height)) {
                err.height = 'Please enter a valid pixel value';
            } else if (height && height < 15) {
                err.height = 'Please enter a value greater than 14';
            } else err.height = '';
        }

        setErrorMessages((prevState) => {
            return { ...prevState, ...err }
        })
    }

    const handleWidthHeightChange = (e) => {
        const targetName = e.target.name
        const targetValue = e.target.value
        let dimension: any = { [targetName]: targetValue }
        if (formData.lockAspectRatio) {
            if (targetValue && isPositiveNumber(targetValue)) {
                const imageRatio = Number(oldWHRef.current.width) / Number(oldWHRef.current.height);
                dimension = getNewImageSize(imageRatio, targetValue, targetName);
                oldWHRef.current.height = dimension.height;
                oldWHRef.current.width = dimension.width;
            }
        }
        else {
            if (targetValue && isPositiveNumber(targetValue)) {
                oldWHRef.current[targetName] = targetValue;
            }
        }
        setFormData((prevState) => {
            return { ...prevState, ...dimension, }
        });
        handleErrorOnWidthHeight(dimension);
    }
    const handleWidthHeightBlur = (event) => {
        const { name, value } = event.target;
        if (!value || !isPositiveNumber(value)) {
            setFormData((prevState) => {
                return { ...prevState, [name]: oldWHRef.current[name] }
            });
        }
    }
    const setInitialWidthHeight = (naturalImageWH: naturalImageWHTypes) => {
        oldWHRef.current = { ...naturalImageWH, widthScale: 100, heightScale: 100 };
        setFormData((prevState) => {
            return { ...prevState, ...naturalImageWH, widthScale: 100, heightScale: 100 }
        });
    }
    useEffect(() => {
        if (naturalImageWH && (attributes.width || attributes.height)) {
            setInitialWidthHeight(naturalImageWH);
        }
    }, [naturalImageWH])

    useEffect(() => {
        if (isAuto) {
            updateFormData({
                width: '',
                height: ''
            })
            return;
        }
        const { width, height } = formData;
        updateFormData({
            width: width ? toFixedIfDecimal(width) : width,
            height: height ? toFixedIfDecimal(height) : height
        })
    }, [formData, isAuto])

    useEffect(() => {
        let errorExist = false
        Object.values(errorMessage).some((message) => {
            return (errorExist = Boolean(message))
        })
        if (hasError.value !== errorExist) hasError.callback(errorExist)
    }, [errorMessage])
    return (
        <div className={styles['scrte--field-size-container']} >
            <Checkbox
                name='size-auto'
                checked={isAuto}
                className={styles['scrte--auto-width-height']}
                onChange={(e) => {
                    setIsAuto(e.target.checked)
                    if (e.target.checked) setErrorMessages({ width: '', height: '' })
                    setInitialWidthHeight(e.target.checked ? { width: '', height: '' } : naturalImageWH)
                }}
                text="Auto adjust dimensions"
                data-test-id="cs-scrte-image-auto"
            />
            <div className={styles['scrte--field-wrap']}>
                <Field>
                    <FieldLabel disabled={isAuto} version='v2' htmlFor='width'>Width (px)</FieldLabel>
                    <TextInput
                        disabled={isAuto}
                        version="v2"
                        value={formData.width ? toFixedIfDecimal(formData.width) : ''}
                        type="number"
                        placeholder={isAuto ? "Auto" : "Enter a width"}
                        name="width"
                        onChange={handleWidthHeightChange}
                        error={!!errorMessage.width}
                        onBlur={handleWidthHeightBlur}
                        testId={"cs-scrte-image-width"}
                    />
                    {
                        errorMessage.width &&
                        <ValidationMessage
                            version='v2'
                            className={styles['scrte--validation-error']}
                        >
                            {errorMessage.width}
                        </ValidationMessage>
                    }
                </Field>
                <Field>
                    <FieldLabel disabled={isAuto} version='v2' htmlFor='height'>Height (px)</FieldLabel>
                    <TextInput
                        disabled={isAuto}
                        version="v2"
                        value={formData.height ? toFixedIfDecimal(formData.height) : ''}
                        type="number"
                        placeholder={isAuto ? "Auto" : "Enter a height"}
                        name="height"
                        onChange={handleWidthHeightChange}
                        error={!!errorMessage.height}
                        onBlur={handleWidthHeightBlur}
                        testId={"cs-scrte-image-height"}
                    />
                    {
                        errorMessage.height &&
                        <ValidationMessage
                            version='v2'
                            className={styles['scrte--validation-error']}
                        >
                            {errorMessage.height}
                        </ValidationMessage>
                    }
                </Field>
            </div>
            <div className='ToggleWrap'>
                <ToggleSwitch
                    disabled={isAuto}
                    label="Lock aspect ratio"
                    name="lockAspectRatio"
                    checked={formData.lockAspectRatio}
                    onChange={handleToggle}
                    testId={"scrte-image-keep-ratio"}
                />
            </div>
        </div>
    )
}

export default ImageSizeField